export const REMARK_PATTERN = /^\[([A-Z0-9/\w\s.]+) (\d+:\d+)\] (.+)$/i;

export function terminalFallback(airline) {
  if (!airline) return "TBIT";
  return airline.toUpperCase() === "AC" ? "T6" : "TBIT";
}

/**
 * Appends a timestamped, attributed remark to an existing remark string.
 * Format: "[AIRLINE/Name HH:MM] text"
 */
export function appendRemark(existingRemark, text, airline, sccoName) {
  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const who = sccoName ? airline + "/" + sccoName : airline;
  const tag = "[" + who + " " + time + "] " + text.trim();
  const sep = String.fromCharCode(10);
  return existingRemark ? existingRemark + sep + tag : tag;
}

// Fields synced to all entries sharing the same outbound or inbound flight number.
const SHARED_OUT_FIELDS = ["outFlt", "outAirline", "dest", "outGate", "std", "etd", "atd"];
const SHARED_IN_FIELDS  = ["inFlt",  "inAirline",  "origin", "inGate", "sta", "eta", "ata"];

/**
 * Finds all entries that share the same outbound or inbound flight as `updated`,
 * and returns a merged list of sibling entries with the shared fields patched in.
 * Entries that share both flights get both patches applied.
 */
export function findSiblingUpdates(updated, entries) {
  const hasSameOutFlt = (e) =>
    e.id !== updated.id &&
    e.outFlt && updated.outFlt &&
    e.outFlt.trim().toUpperCase() === updated.outFlt.trim().toUpperCase() &&
    (e.outAirline || "").toUpperCase() === (updated.outAirline || "").toUpperCase();

  const hasSameInFlt = (e) =>
    e.id !== updated.id &&
    e.inFlt && updated.inFlt &&
    e.inFlt.trim().toUpperCase() === updated.inFlt.trim().toUpperCase() &&
    (e.inAirline || "").toUpperCase() === (updated.inAirline || "").toUpperCase();

  const outPatch = Object.fromEntries(SHARED_OUT_FIELDS.map(k => [k, updated[k] ?? ""]));
  const inPatch  = Object.fromEntries(SHARED_IN_FIELDS.map(k =>  [k, updated[k] ?? ""]));

  const outSiblings = entries.filter(hasSameOutFlt);
  const inSiblings  = entries.filter(hasSameInFlt)
    .filter(sib => !outSiblings.some(s => s.id === sib.id));

  const mergedOut = outSiblings.map(sib =>
    hasSameInFlt(sib) ? { ...sib, ...outPatch, ...inPatch } : { ...sib, ...outPatch }
  );

  return [...mergedOut, ...inSiblings.map(sib => ({ ...sib, ...inPatch }))];
}

/**
 * Compares an imported entry against its existing counterpart and returns only
 * the DB-column fields that have actually changed. Returns null if nothing changed.
 */
export function buildImportPatch(imported, existing) {
  const patch = {};
  if (imported.eta && imported.eta !== existing.eta) patch.eta         = imported.eta;
  if (imported.sta        !== existing.sta)          patch.sta         = imported.sta        || "";
  if (imported.std        !== existing.std)          patch.std         = imported.std        || "";
  if (imported.etd        !== existing.etd)          patch.etd         = imported.etd        || "";
  if (imported.ttlPax     !== existing.ttlPax)       patch.ttl_pax     = imported.ttlPax     || "";
  if (imported.ttlBags    !== existing.ttlBags)      patch.ttl_bags    = imported.ttlBags    || "";
  if (imported.origin     !== existing.origin)       patch.origin      = imported.origin     || "";
  if (imported.dest       !== existing.dest)         patch.dest        = imported.dest       || "";
  if (imported.inAirline  !== existing.inAirline)    patch.in_airline  = imported.inAirline  || "";
  if (imported.outAirline !== existing.outAirline)   patch.out_airline = imported.outAirline || "";
  if (imported.airline    !== existing.airline)      patch.airline     = imported.airline    || "";
  if (imported.section    !== existing.section)      patch.section     = imported.section    || "";
  if (imported.cancelled  !== existing.cancelled)    patch.cancelled   = imported.cancelled  || false;
  if (imported.inGate  && !existing.inGate)          patch.in_gate     = imported.inGate;
  if (imported.outGate && !existing.outGate)         patch.out_gate    = imported.outGate;
  return Object.keys(patch).length > 0 ? patch : null;
}
